import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Player } from '../../types/player';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend
} from 'recharts';

interface ComparisonRadarChartProps {
  player1: Player;
  player2: Player;
}

const ComparisonRadarChart: React.FC<ComparisonRadarChartProps> = ({ player1, player2 }) => {
  const { t } = useTranslation();

  const getPlayerName = (player: Player) => {
    const name = player.player_name || player.nombre_transfermarkt || '';
    return name.split('(')[0].trim();
  };

  // Normalize values to 0-100 scale
  const normalizeValue = (value: number | undefined, max: number) => {
    if (!value) return 0;
    return Math.min((value / max) * 100, 100);
  };

  const data = [
    {
      attribute: t('comparison.goals'),
      player1: normalizeValue(player1.goles || player1.goles_por_90, 1),
      player2: normalizeValue(player2.goles || player2.goles_por_90, 1),
      fullMark: 100
    },
    {
      attribute: t('comparison.assists'),
      player1: normalizeValue(player1.asistencias || player1.asistencias_por_90, 1),
      player2: normalizeValue(player2.asistencias || player2.asistencias_por_90, 1),
      fullMark: 100
    },
    {
      attribute: t('comparison.xG'),
      player1: normalizeValue(player1.xg_por_90, 1),
      player2: normalizeValue(player2.xg_por_90, 1),
      fullMark: 100
    },
    {
      attribute: t('comparison.xA'),
      player1: normalizeValue(player1.xag_por_90, 1),
      player2: normalizeValue(player2.xag_por_90, 1),
      fullMark: 100
    },
    {
      attribute: t('comparison.progressions'),
      player1: normalizeValue(
        (player1.progresion_conduccion || 0) + (player1.progresion_pase || 0) + (player1.progresion_regate || 0),
        300
      ),
      player2: normalizeValue(
        (player2.progresion_conduccion || 0) + (player2.progresion_pase || 0) + (player2.progresion_regate || 0),
        300
      ),
      fullMark: 100
    },
    {
      attribute: t('comparison.matchesPlayed'),
      player1: normalizeValue(player1.partidos_jugados || player1.partidos, 50),
      player2: normalizeValue(player2.partidos_jugados || player2.partidos, 50),
      fullMark: 100
    }
  ];

  return (
    <div className="w-full h-96">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data}>
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis 
            dataKey="attribute" 
            tick={{ fill: '#6b7280', fontSize: 12 }}
          />
          <PolarRadiusAxis 
            angle={90} 
            domain={[0, 100]} 
            tick={{ fill: '#6b7280', fontSize: 10 }}
          />
          <Radar
            name={getPlayerName(player1)}
            dataKey="player1"
            stroke="#3b82f6"
            fill="#3b82f6"
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <Radar
            name={getPlayerName(player2)}
            dataKey="player2"
            stroke="#ef4444"
            fill="#ef4444"
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="circle"
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ComparisonRadarChart;
